/**
 * Admin's single-line text input — a dialect skin over the shared `Input` core
 * (BL-1713). The core owns the control (the invalid state, the phone sheet's
 * touch size); this file keeps admin's props, which 54 call sites are written
 * against, and declares admin's look: grey border, red when invalid, teal
 * focus ring. `FormField` draws the label and the inline error around it.
 */
'use client';

import type { KeyboardEvent } from 'react';
import { Input as UiInput, SHEET_FIELD_BORDER, type InputDialect } from '@auctogon/ui/web';
import { READ_VALUE_CLASSES, useReadView } from './ReadViewContext';
import { FIELD_ERROR_FOCUS, SHEET_FIELD_ERROR_FOCUS, SHEET_FIELD_FOCUS } from './sheet-field-focus';


/**
 * Admin's look, verbatim from the pre-merge component.
 *
 * `nonTouch`: a 44px floor below `sm` — the platform minimum for a thumb (Apple
 * HIG 44pt, WCAG 2.5.8 24px). `py-2` alone renders 38px, which is under it.
 * Deliberately a breakpoint and NOT a `size` prop like `Select`'s: that prop
 * exists because `LotTable` decides by CONTAINER width (a table can be narrow
 * inside a wide window), a question a media query can't ask. Here the question
 * really is "is this a phone", so the answer belongs in the primitive — nobody
 * has to remember to pass it on a new field. At `sm` and up this is inert, so
 * desktop is unchanged.
 *
 * `touch`: BL-2108 — inside a phone sheet the field is the sheet's 48px / 16px
 * touch field (BUG-1860), the same box as the shared `Select`'s `touch` trigger.
 */
const ADMIN_INPUT_DIALECT: InputDialect = {
  base:
    'w-full px-3 py-2 rounded-lg text-gray-900 border ' +
    'disabled:bg-gray-50 disabled:text-[var(--ink-500)] disabled:cursor-not-allowed',
  resting: 'border-gray-300',
  // BL-2548: the sheet's light resting border (1.26:1, ADR 051 sheet-field amendment).
  touchResting: SHEET_FIELD_BORDER,
  invalid: 'border-red-600',
  // BUG-2057: the sheet's one focus look; every other field keeps the classic ring.
  touch: `min-h-12 text-body-lg ${SHEET_FIELD_FOCUS}`,
  nonTouch: 'min-h-11 sm:min-h-0 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary',
  // BL-2472: in error the field stays red while focused, on the sheet and off it.
  invalidTouch: `min-h-12 text-body-lg ${SHEET_FIELD_ERROR_FOCUS}`,
  invalidNonTouch: `min-h-11 sm:min-h-0 ${FIELD_ERROR_FOCUS}`,
};

export interface TextInputProps {
  value: string;
  /**
   * Native input type — `text` (default), `email`, `tel`, or `password`.
   *
   * Deliberately NOT the full HTML set: numeric and date inputs have dedicated
   * design-system components (`NumberInput`, `DateTimePicker`) per
   * ui-standards.md, and widening this would let a call site bypass them. Added
   * in BL-1170 so the Edit member modal's email/phone fields could move off raw
   * `<input>` without losing their keyboard type on mobile; `password` added in
   * BUG-1344 for the lienholder modal's masked bank-account fields — it has no
   * dedicated primitive to bypass, so it doesn't reopen the hole this list
   * exists to close. `url` added in BL-1955 for the tenant Base URL and the
   * Website Branding logo link and social URLs: no dedicated primitive to
   * bypass either, and it keeps the browser's URL keyboard on mobile.
   */
  type?: 'text' | 'email' | 'tel' | 'password' | 'url';
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  list?: string;
  className?: string;
  id?: string;
  /**
   * Native `required` — the browser blocks submit on an empty value. Added in
   * BL-1955 for the tenant modal's Tenant Name and Slug, which rely on it.
   */
  required?: boolean;
  /** Native `autoFocus` — focus on mount (BL-1955: the logo-URL edit box). */
  autoFocus?: boolean;
  /** Native maxLength — caps input at N characters (e.g. make/model at 100). */
  maxLength?: number;
  /**
   * Native `inputMode` — picks the on-screen keyboard without changing `type`.
   *
   * Added in BL-1766 for the tax-rate per-invoice caps. Those are money, so
   * ui-standards.md would normally route them to `NumberInput`, but that
   * primitive collapses a cleared field to `0` and these fields must keep
   * blank ("no cap") distinct from `0` ("a real cap of zero — collects no
   * local tax at all"). BL-1869 adds an empty mode to `NumberInput`; until it
   * lands, this gives the field the numeric keypad it should have had.
   *
   * It does NOT reopen the hole the `type` list closes: `inputMode` changes
   * the keyboard only, never the parsing or the value, so a caller still
   * cannot get numeric-input BEHAVIOR by going around `NumberInput`.
   */
  inputMode?: 'text' | 'decimal' | 'numeric' | 'tel' | 'email' | 'url' | 'search';
  /**
   * Renders the value as non-editable while keeping it addressable by its
   * accessible name (a screen reader still reads it).
   *
   * Deliberately NOT `disabled`, which reads as "unavailable": a derived value
   * is a real figure the operator reads. Added in BL-1147 for a commission
   * tier's derived floor.
   */
  readOnly?: boolean;
  /**
   * Native `tabIndex`. Only `-1` is expected: a read-only derived value (a
   * commission tier's floor) that should not be a Tab stop (BUG-2102, Dan Saper
   * 2026-10-15). An editable input keeps the default order.
   */
  tabIndex?: -1;
  /** Accessibility — flips the red invalid border + announces to screen readers. */
  invalid?: boolean;
  /** Accessibility — id of an element describing this input (e.g. an inline error message). */
  'aria-describedby'?: string;
  /** Test hook. Forwarded to the rendered element so behavioral tests can address it. */
  'data-testid'?: string;
  /**
   * Accessibility — accessible name for inputs with no visible <label>.
   * Added in BL-913: call sites migrating off a raw <input> carried an
   * `aria-label` that this component silently dropped (props are destructured,
   * not spread), leaving the field with no accessible name and no type error.
   */
  'aria-label'?: string;
  /**
   * Receives the `<input>`. Hand it React Hook Form's `field.ref` from a
   * `Controller` so a refused save can move focus here (`shouldFocusError` /
   * `setFocus`) — ADR 016 — Form Validation § 8, BL-2078.
   */
  inputRef?: (el: HTMLInputElement | null) => void;
  /**
   * Native key handler. Added in BUG-2080 for the rich-text editor's inline URL
   * fields, where Enter adds the link or video and Escape cancels.
   */
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
}

/**
 * Single-line text input styled to match the design system.
 *
 * @param value - Controlled value
 * @param type - Native input type: `text` (default), `email`, `tel`, `password`, or `url`
 * @param onChange - Called with the new string value on every keystroke
 * @param placeholder - Optional placeholder text
 * @param disabled - Disables the input
 * @param list - Optional datalist id for autocomplete suggestions
 * @param className - Additional Tailwind classes
 * @param id - HTML id attribute
 * @param required - Native required: blocks form submit while empty
 * @param autoFocus - Focus the input on mount
 * @param maxLength - Native maxLength — caps input at N characters
 * @param readOnly - Renders the value non-editable, still readable by its name
 * @param tabIndex - `-1` takes a read-only derived value out of the Tab order
 * @param invalid - When true, applies the red error border and sets aria-invalid
 * @param aria-describedby - Id of a helper/error element so screen readers can announce it
 * @param aria-label - Accessible name when there is no visible <label>
 * @param inputRef - Receives the input element (React Hook Form's `field.ref`)
 * @param onKeyDown - Native key handler (Enter / Escape on an inline field)
 * @returns The input.
 */
export default function TextInput({
  value,
  type = 'text',
  onChange,
  placeholder,
  disabled,
  list,
  className,
  id,
  required,
  autoFocus,
  maxLength,
  inputMode,
  readOnly,
  tabIndex,
  invalid,
  'aria-describedby': ariaDescribedBy,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  inputRef,
  onKeyDown,
}: TextInputProps) {
  // Hooks before the conditional return (rule #7).
  const readView = useReadView();
  // A password is never shown as text, even in a read region.
  if (readView && type !== 'password') {
    return (
      <span id={id} data-testid={dataTestId} aria-label={ariaLabel} className={READ_VALUE_CLASSES}>
        {value}
      </span>
    );
  }
  return (
    <UiInput
      dialect={ADMIN_INPUT_DIALECT}
      ref={inputRef}
      data-testid={dataTestId}
      type={type}
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      tabIndex={tabIndex}
      required={required}
      autoFocus={autoFocus}
      list={list}
      maxLength={maxLength}
      inputMode={inputMode}
      invalid={invalid}
      aria-describedby={ariaDescribedBy}
      aria-label={ariaLabel}
      className={className}
    />
  );
}
